'use client';

// - The button appears when the user scrolls **300px down** and disappears when scrolling back up.
// - Clicking the button scrolls to the top **with smooth animation**.
// - On **large screens**, the button **expands on hover**, revealing the "Back to top" text.
// - On **smaller screens (tablet & mobile)**, the button remains compact (fixed width of `w-40`).
// - On larger screens, the button **expands smoothly** (`hover:w-120`), ensuring a clean transition.
// - The button uses `aria-label="Back to top"` for screen readers.
// - Uses `useEffect` to listen for scroll events and clean up the event listener on unmount.
// - Uses `useMediaQuery` to detect screen size and apply different behaviors based on the breakpoint.

import { ChevronUpIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet_medium } from 'helpers/utils/preferredScreenSizes';

const BackToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [ismobile] = useMediaQuery(tablet_medium);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className={`group fixed bottom-100 right-0 flex h-38 ${
        !ismobile ? 'w-40' : 'w-40 hover:w-120'
      } items-center justify-start rounded-l-md bg-[#149064] transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={scrollToTop}
    >
      <div
        className="ml-10 flex h-18 w-18 items-center justify-center rounded-full bg-white shadow-lg"
        aria-label="Back to top"
      >
        <ChevronUpIcon className=" h-16 w-16 text-[#149064]" stroke="currentColor" strokeWidth="2" />
      </div>

      {ismobile && (
        <p className="invisible ml-auto mr-5 whitespace-nowrap text-15 text-white transition-all duration-200 group-hover:visible">
          Back to top
        </p>
      )}
    </div>
  );
};

export default BackToTopButton;
